import type {
  AlertEvent,
  AlertRule,
  ApplicationWithStats,
  Condition,
  CreateAlertRule,
  DiscordTarget,
  MessageTypeWithStats,
} from '@owl/shared';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { Icon } from '../components/icons';
import { ChannelBadge, SeverityChip } from '../components/shared';
import { activateOnKey, isFromRowActions } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { timeAgo } from '../lib/time';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useLoader } from '../lib/useLoader';

type Prefill = { applicationId?: string; messageTypeId?: string; pattern?: string };

function initialDiscordTarget(value: CreateAlertRule['channels']['discord']): DiscordTarget {
  if (value === 'status' || value === 'reports' || value === 'alerts') return value;
  return 'alerts';
}

export function Alerts({ ctx, prefill }: { ctx: ShellCtx; prefill?: Prefill }) {
  const refreshTick = ctx.refreshTick;
  const [tab, setTab] = useState<'rules' | 'events'>('rules');
  const [rules, setRules] = useState<AlertRule[] | null>(null);
  const [events, setEvents] = useState<AlertEvent[] | null>(null);
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [mts, setMts] = useState<MessageTypeWithStats[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<Prefill | null>(prefill ?? null);
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const { push } = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      const [r, e, a, m] = await Promise.all([
        api.listAlertRules(),
        api.listAlertEvents({ environment: ctx.environment, limit: 100 }),
        api.listApplications(ctx.environment),
        api.listMessageTypes(),
      ]);
      setRules(r.items);
      setEvents(e.items);
      setApps(a.items);
      setMts(m.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [ctx.environment]);

  useLoader(load, refreshTick);

  useEffect(() => {
    if (prefill) setCreating(prefill);
  }, [prefill]);

  async function toggleRule(rule: AlertRule) {
    try {
      const next = rule.status === 'active' ? 'paused' : 'active';
      await api.updateAlertRule(rule.id, { status: next });
      push(`${rule.name} → ${next}`);
      load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Toggle failed');
    }
  }

  async function deleteRule(rule: AlertRule) {
    const ok = await confirm({
      title: 'Delete alert rule',
      sub: rule.name,
      body: 'The rule stops firing right away. Past alert events are kept.',
      confirmLabel: 'Delete rule',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteAlertRule(rule.id);
      push('Rule deleted');
      load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Delete failed');
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Alerts</div>
          <div className="view-sub">{rules?.length ?? 0} rules</div>
        </div>
        <button type="button" className="cta" onClick={() => setCreating({})}>
          <Icon.Plus size={13} /> New rule
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${tab === 'rules' ? 'active' : ''}`}
          onClick={() => setTab('rules')}
        >
          Rules ({rules?.length ?? 0})
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'events' ? 'active' : ''}`}
          onClick={() => setTab('events')}
        >
          Events ({events?.length ?? 0})
        </button>
      </div>

      {tab === 'rules' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Cooldown</th>
                <th>Triggers 7d</th>
                <th>Last</th>
                <th>Channels</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rules?.map((r) => (
                <tr
                  key={r.id}
                  className="data-row"
                  tabIndex={0}
                  onClick={(e) => {
                    if (isFromRowActions(e)) return;
                    setEditing(r);
                  }}
                  onKeyDown={(e) => activateOnKey(e, () => setEditing(r))}
                >
                  <td>
                    <strong>{r.name}</strong>
                    {r.description && (
                      <div className="dim" style={{ fontSize: 11 }}>
                        {r.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <SeverityChip severity={r.severity} />
                  </td>
                  <td className="dim">{r.status}</td>
                  <td className="mono">{r.cooldownMinutes}m</td>
                  <td className="mono">{r.triggerCount7d}</td>
                  <td className="dim">{timeAgo(r.lastTriggeredAt)}</td>
                  <td>
                    {(r.channels.email ?? []).length > 0 && <ChannelBadge channel="email" />}
                    {r.channels.discord && <ChannelBadge channel="discord" />}
                    {r.channels.webhook && <ChannelBadge channel="webhook" />}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => toggleRule(r)}
                        title="Toggle"
                      >
                        {r.status === 'active' ? <Icon.Pause size={12} /> : <Icon.Play size={12} />}
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => deleteRule(r)}
                        title="Delete"
                      >
                        <Icon.Trash size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              )) ?? null}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'events' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Rule</th>
                <th>Application</th>
                <th>Channels</th>
              </tr>
            </thead>
            <tbody>
              {events?.map((ev) => {
                const rule = rules?.find((r) => r.id === ev.ruleId);
                const app = apps.find((a) => a.id === ev.applicationId);
                return (
                  <tr key={ev.id}>
                    <td className="mono dim">{timeAgo(ev.triggeredAt)}</td>
                    <td>{rule?.name ?? <span className="dim">(deleted)</span>}</td>
                    <td>{app?.name ?? <span className="dim">(deleted)</span>}</td>
                    <td>
                      {ev.channelsResult.map((c) => (
                        <ChannelBadge key={c.channel} channel={c.channel} status={c.status} />
                      ))}
                    </td>
                  </tr>
                );
              }) ?? null}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <AlertRuleModal
          existing={editing}
          prefill={creating ?? undefined}
          apps={apps}
          mts={mts}
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(null);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function AlertRuleModal({
  existing,
  prefill,
  apps,
  mts,
  onClose,
  onSaved,
}: {
  existing?: AlertRule | null;
  prefill?: Prefill;
  apps: ApplicationWithStats[];
  mts: MessageTypeWithStats[];
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeKey(onClose);
  const initial = useMemo<CreateAlertRule>(() => {
    if (existing) {
      return {
        name: existing.name,
        description: existing.description ?? undefined,
        severity: existing.severity,
        scopeApplications: existing.scopeApplications,
        scopeMessageTypes: existing.scopeMessageTypes,
        condition: existing.condition,
        channels: existing.channels,
        cooldownMinutes: existing.cooldownMinutes,
        groupSimilar: existing.groupSimilar,
        status: existing.status,
      };
    }
    return {
      name: '',
      description: undefined,
      severity: 'medium',
      scopeApplications: prefill?.applicationId ? [prefill.applicationId] : 'all',
      scopeMessageTypes: prefill?.messageTypeId ? [prefill.messageTypeId] : 'all',
      condition: prefill?.pattern
        ? { type: 'content_match', pattern: prefill.pattern, isRegex: false }
        : { type: 'any_match' },
      channels: { email: [], discord: true },
      cooldownMinutes: 5,
      groupSimilar: true,
      status: 'active',
    };
  }, [existing, prefill]);

  const [data, setData] = useState<CreateAlertRule>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discordEnabled, setDiscordEnabled] = useState(
    initial.channels.discord !== undefined && initial.channels.discord !== false,
  );
  const [discordTarget, setDiscordTarget] = useState<DiscordTarget>(
    initialDiscordTarget(initial.channels.discord),
  );

  function setCondition(c: Condition) {
    setData((d) => ({ ...d, condition: c }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload: CreateAlertRule = {
        ...data,
        channels: {
          ...data.channels,
          discord: discordEnabled ? discordTarget : undefined,
        },
      };
      if (existing) {
        await api.updateAlertRule(existing.id, payload);
      } else {
        await api.createAlertRule(payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card" style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div className="modal-title">
            {existing ? `Edit "${existing.name}"` : 'New alert rule'}
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Name</span>
            <input
              className="form-input"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">Severity</span>
            <select
              className="form-select"
              value={data.severity}
              onChange={(e) =>
                setData({ ...data, severity: e.target.value as CreateAlertRule['severity'] })
              }
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </label>

          <fieldset className="form-row">
            <legend className="form-label">Scope · applications</legend>
            <ScopeSelect
              all="all applications"
              options={apps.map((a) => ({ id: a.id, label: a.name }))}
              value={data.scopeApplications}
              onChange={(v) => setData({ ...data, scopeApplications: v })}
            />
          </fieldset>

          <fieldset className="form-row">
            <legend className="form-label">Scope · message types</legend>
            <ScopeSelect
              all="all message types"
              options={mts.map((m) => ({ id: m.id, label: m.name }))}
              value={data.scopeMessageTypes}
              onChange={(v) => setData({ ...data, scopeMessageTypes: v })}
            />
          </fieldset>

          <ConditionEditor condition={data.condition} setCondition={setCondition} />

          <label className="form-row">
            <span className="form-label">Email recipients (comma-separated)</span>
            <input
              className="form-input"
              value={(data.channels.email ?? []).join(', ')}
              onChange={(e) =>
                setData({
                  ...data,
                  channels: {
                    ...data.channels,
                    email: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  },
                })
              }
              placeholder="ops@example.com"
            />
          </label>

          <fieldset className="form-row">
            <legend className="form-label">Discord</legend>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <input
                type="checkbox"
                checked={discordEnabled}
                onChange={(e) => setDiscordEnabled(e.target.checked)}
              />
              Send to Discord
            </label>
            {discordEnabled && (
              <select
                className="form-select"
                value={discordTarget}
                onChange={(e) => setDiscordTarget(e.target.value as DiscordTarget)}
                style={{ marginTop: 8 }}
              >
                <option value="alerts">OWL Alerts</option>
                <option value="status">OWL Status</option>
                <option value="reports">OWL Reports</option>
              </select>
            )}
          </fieldset>

          <label className="form-row">
            <span className="form-label">Webhook URL (signed with ALERT_WEBHOOK_SECRET)</span>
            <input
              className="form-input"
              type="url"
              value={data.channels.webhook ?? ''}
              onChange={(e) =>
                setData({
                  ...data,
                  channels: { ...data.channels, webhook: e.target.value.trim() || undefined },
                })
              }
              placeholder="https://joseph.example/api/hooks/owl"
            />
          </label>

          <label className="form-row">
            <span className="form-label">Cooldown (minutes)</span>
            <input
              className="form-input"
              type="number"
              min={0}
              max={1440}
              value={data.cooldownMinutes}
              onChange={(e) => setData({ ...data, cooldownMinutes: Number(e.target.value) })}
            />
          </label>

          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Saving…' : 'Save rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScopeSelect({
  all,
  options,
  value,
  onChange,
}: {
  all: string;
  options: { id: string; label: string }[];
  value: 'all' | string[];
  onChange: (v: 'all' | string[]) => void;
}) {
  const isAll = value === 'all';
  const selected = isAll ? [] : (value as string[]);
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <input
          type="checkbox"
          checked={isAll}
          onChange={(e) => onChange(e.target.checked ? 'all' : [])}
        />
        {all}
      </label>
      {!isAll && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {options.map((opt) => (
            <button
              type="button"
              key={opt.id}
              className={`mt-toggle ${selected.includes(opt.id) ? 'active' : ''}`}
              onClick={() =>
                onChange(
                  selected.includes(opt.id)
                    ? selected.filter((x) => x !== opt.id)
                    : [...selected, opt.id],
                )
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConditionEditor({
  condition,
  setCondition,
}: {
  condition: Condition;
  setCondition: (c: Condition) => void;
}) {
  // The kind select shares its row with the inputs each kind reveals, so the label
  // is tied to it by id rather than by wrapping the whole row.
  const typeId = useId();
  return (
    <div className="form-row">
      <label className="form-label" htmlFor={typeId}>
        Condition
      </label>
      <select
        id={typeId}
        className="form-select"
        value={condition.type}
        onChange={(e) => {
          const t = e.target.value as Condition['type'];
          if (t === 'any_match') setCondition({ type: 'any_match' });
          else if (t === 'content_match')
            setCondition({ type: 'content_match', pattern: '', isRegex: false });
          else if (t === 'metadata_match')
            setCondition({ type: 'metadata_match', key: '', operator: '=', value: '' });
          else if (t === 'threshold')
            setCondition({ type: 'threshold', count: 5, windowMinutes: 10 });
        }}
      >
        <option value="any_match">Any match</option>
        <option value="content_match">Content match</option>
        <option value="metadata_match">Metadata match</option>
        <option value="threshold">Threshold (count over window)</option>
      </select>

      {condition.type === 'content_match' && (
        <>
          <input
            className="form-input"
            placeholder="pattern"
            value={condition.pattern}
            onChange={(e) => setCondition({ ...condition, pattern: e.target.value })}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={condition.isRegex}
              onChange={(e) => setCondition({ ...condition, isRegex: e.target.checked })}
            />
            Treat as regex
          </label>
        </>
      )}

      {condition.type === 'metadata_match' && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="form-input"
            placeholder="key"
            value={condition.key}
            onChange={(e) => setCondition({ ...condition, key: e.target.value })}
            style={{ flex: 1 }}
          />
          <select
            className="form-select"
            value={condition.operator}
            onChange={(e) =>
              setCondition({
                ...condition,
                operator: e.target.value as typeof condition.operator,
              })
            }
          >
            <option value="=">=</option>
            <option value="!=">≠</option>
            <option value="contains">contains</option>
            <option value=">">{'>'}</option>
            <option value="<">{'<'}</option>
          </select>
          <input
            className="form-input"
            placeholder="value"
            value={condition.value}
            onChange={(e) => setCondition({ ...condition, value: e.target.value })}
            style={{ flex: 1 }}
          />
        </div>
      )}

      {condition.type === 'threshold' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          More than
          <input
            className="form-input"
            type="number"
            min={1}
            value={condition.count}
            onChange={(e) => setCondition({ ...condition, count: Number(e.target.value) })}
            style={{ width: 80 }}
          />
          matches in
          <input
            className="form-input"
            type="number"
            min={1}
            value={condition.windowMinutes}
            onChange={(e) => setCondition({ ...condition, windowMinutes: Number(e.target.value) })}
            style={{ width: 80 }}
          />
          minutes
        </div>
      )}
    </div>
  );
}
